import {TypeLocalNameEnum} from 'domain/usecases';
import {QueryParamsForTaggingEnum} from 'presentation/constants';
import {
	GynecologistEnum,
	gynecologyNames,
	WellBornEndpoints,
} from 'presentation/modules/booking/shared/constants/beBornWell';
import {useWellBornEligibility} from 'presentation/modules/booking/shared/hooks/useWellBornEligibility';
import {getWellBornEligibility} from 'presentation/modules/booking/shared/services/getWellBornEligibility';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {Specialty} from 'presentation/utils';
import {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {
	EligibilityFlowParams,
	NegativeProgramFlowParams,
	SpecialtyProgramFlowParams,
	UseBeBornWellProps,
} from './types';

export function useBeBornWell({
	handleCheckDuplicity,
	handleSelectCard,
	setCheckBookingModal,
}: UseBeBornWellProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const [openBeBornWellDrawer, setOpenBeBornWellDrawer] = useState(false);
	const [showBeBornWellCard, setShowBeBornWellCard] = useState(false);
	const [showBeBornWellNotRegister, setShowBeBornWellNotRegister] = useState(false);
	const [selectedSpecialty, setSelectedSpecialty] = useState<Specialty | null>(null);
	const [hasProcessedParams, setHasProcessedParams] = useState(false);
	const {setEligibilityData} = useWellBornEligibility();
	const [searchParams] = useSearchParams();

	const {ufCode, cityName, typeLocalName} = bookingData;

	const handleNotPregnantClick = async () => {
		setOpenBeBornWellDrawer(false);
		const alreadyScheduled = await handleCheckDuplicity(GynecologistEnum.CODE);

		if (!alreadyScheduled) {
			const specialty: Specialty = {
				code: GynecologistEnum.CODE,
				description: GynecologistEnum.CLINICAL_GYNECOLOGY,
				formattedText: GynecologistEnum.CLINICAL_GYNECOLOGY,
				telemedicine: '',
			};
			await handleSelectCard(specialty);
		}
	};

	const handleShowBeBornWellCard = () => {
		setOpenBeBornWellDrawer(false);
		setShowBeBornWellCard(true);
	};

	const handleShowBeBornWellNotRegister = () => {
		setOpenBeBornWellDrawer(false);
		setShowBeBornWellNotRegister(true);
	};

	const handleContinueToBooking = () => {
		if (selectedSpecialty) {
			handleSelectCard(selectedSpecialty);
		}
	};

	const handleNegativeProgramFlow = ({cidadePrograma, specialty}: NegativeProgramFlowParams) => {
		if (cidadePrograma === WellBornEndpoints.WELL_BORN) {
			setShowBeBornWellNotRegister(true);
		} else {
			handleSelectCard(specialty);
		}
	};

	const handleSpecialtyProgramFlow = ({
		cidadePrograma,
		usuarioNoPrograma,
		usuarioEmCaptacao,
		specialty,
	}: SpecialtyProgramFlowParams) => {
		if (usuarioNoPrograma || usuarioEmCaptacao) {
			if (
				cidadePrograma === WellBornEndpoints.WELL_BORN ||
				cidadePrograma === WellBornEndpoints.CARRY_PREGNANCY_WELL
			) {
				if (cidadePrograma === WellBornEndpoints.WELL_BORN) {
					setShowBeBornWellCard(true);
				} else {
					handleSelectCard(specialty);
				}
			} else {
				handleSelectCard(specialty);
			}
		} else {
			setOpenBeBornWellDrawer(true);
		}
	};

	const handleEligibilityFlow = ({
		cidadePrograma,
		especialidadePrograma,
		usuarioNoPrograma,
		usuarioEmCaptacao,
		usuarioNegativaPrograma,
		specialty,
	}: EligibilityFlowParams) => {
		if (usuarioNegativaPrograma) {
			handleNegativeProgramFlow({cidadePrograma, specialty});
			return;
		}

		if (especialidadePrograma) {
			handleSpecialtyProgramFlow({
				cidadePrograma,
				usuarioNoPrograma,
				usuarioEmCaptacao,
				specialty,
			});
		} else {
			handleSelectCard(specialty);
		}
	};

	const handleSelectCardWrapper = async (specialty: Specialty) => {
		setSelectedSpecialty(specialty);

		const name = (specialty.formattedText || specialty.description || '').toUpperCase();

		if (!gynecologyNames.includes(name as GynecologistEnum)) {
			await handleSelectCard(specialty);
			return;
		}

		try {
			setCheckBookingModal('verifyingAuth');
			const eligibilityResponse = await getWellBornEligibility({
				stateCode: ufCode,
				typeLocalName: bookingData.isManualLocation
					? (typeLocalName ?? TypeLocalNameEnum.CIDADE)
					: TypeLocalNameEnum.CIDADE,
				localName: cityName,
				specialtyCode: specialty.code.toString(),
			});
			setCheckBookingModal('none');

			if (!eligibilityResponse) {
				await handleSelectCard(specialty);
				return;
			}

			setEligibilityData(eligibilityResponse);

			const {
				cidadePrograma,
				especialidadePrograma,
				usuarioNoPrograma,
				usuarioEmCaptacao,
				usuarioNegativaPrograma,
			} = eligibilityResponse;

			if (!cidadePrograma) {
				await handleSelectCard(specialty);
				return;
			}

			handleEligibilityFlow({
				cidadePrograma,
				especialidadePrograma,
				usuarioNoPrograma,
				usuarioEmCaptacao,
				usuarioNegativaPrograma,
				specialty,
			});
		} catch {
			await handleSelectCard(specialty);
		}
	};

	useEffect(() => {
		const shouldShowNotRegister =
			searchParams.get(QueryParamsForTaggingEnum.SHOW_BE_BORN_WELL_NOT_REGISTER) === 'true';

		if (shouldShowNotRegister && !hasProcessedParams) {
			const specialtyCode = searchParams.get(QueryParamsForTaggingEnum.SPECIALTY_CODE);
			const specialtyName = searchParams.get(QueryParamsForTaggingEnum.SPECIALTY_NAME);

			setShowBeBornWellNotRegister(true);

			if (specialtyCode && specialtyName && !selectedSpecialty) {
				setSelectedSpecialty({
					code: specialtyCode,
					description: specialtyName,
					formattedText: specialtyName,
				} as Specialty);
			}

			setHasProcessedParams(true);

			const currentUrl = new URL(window.location.href);
			currentUrl.searchParams.delete(
				QueryParamsForTaggingEnum.SHOW_BE_BORN_WELL_NOT_REGISTER,
			);
			window.history.replaceState({}, '', currentUrl.toString());
		} else if (hasProcessedParams && !shouldShowNotRegister) {
			setHasProcessedParams(false);
		}
	}, [searchParams, selectedSpecialty, hasProcessedParams]);

	return {
		showBeBornWellCard,
		showBeBornWellNotRegister,
		openBeBornWellDrawer,
		setOpenBeBornWellDrawer,
		handleNotPregnantClick,
		handleShowBeBornWellCard,
		handleShowBeBornWellNotRegister,
		handleContinueToBooking,
		selectedSpecialty,
		handleSelectCardWrapper,
	};
}
